import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import {
  FormUserCompletionResponse,
  IntakeFormItem,
  PhodaFormsService,
} from "src/swagger/api.client.generated";
import { SnackbarService } from "@app/common/snackbar-custom/snackbar.service";
@Injectable({
  providedIn: "root",
})
export class SharedService {
  formUserCompletionUpdated = new Subject<FormUserCompletionResponse>();

  constructor(
    private phodaFormsService: PhodaFormsService,
    private snackbarService: SnackbarService
  ) {}

  radioOptions: {
    seasons: ["Winter", "Spring", "Summer", "Autumn"];
    frequency2: ["Zero", "1", "2 or more"];
    frequency5: ["Zero", "1-2", "3-5", ">5"];
    frequency15: ["Zero", "1-5", "6-10", "11-15", ">15"];
    yesNoUndecided: ["Yes", "No", "Undecided"];
    yesNoSometimes: ["Yes", "No", "Sometimes"];
    yesNo: ["Yes", "No"];
  };

  statementApply = [
    "Did not apply to me at all",
    "Applied to me to some degree, or some of the time",
    "Applied to me to a considerable degree, or a good part of the time",
    "Applied to me very much, or most of the time",
  ];

  statementApplyDegree = [
    "Not at all",
    "To a Slight Degree",
    "To a Moderate Degree",
    "To a Great Degree",
    "All the Time",
  ];

  painIntensityQuestions = [
    {
      number: 1,
      model: "worstPainLastWeek",
      question: "Your pain at its worst in the last week?",
    },
    {
      number: 2,
      model: "leastPainLastWeek",
      question: "Your pain at its least in the last week?",
    },
    { number: 3, model: "painAverage", question: "Your pain on average?" },
    {
      number: 4,
      model: "currentPain",
      question: "How much pain do you have right now?",
    },
  ];

  painInterferenceQuestions = [
    { number: 1, model: "generalActivity", question: "Your general activity?" },
    { number: 2, model: "mood", question: "Your mood?" },
    { number: 3, model: "walkingAbility", question: "Your walking ability?" },
    {
      number: 4,
      model: "normalWork",
      question: "Your normal work (both outside the home and housework)?",
    },
    {
      number: 5,
      model: "relationsOtherPeople",
      question: "Your relations with other people?",
    },
    { number: 6, model: "sleep", question: "Your sleep?" },
    { number: 7, model: "lifeEnjoyment", question: "Your enjoyment of life?" },
  ];

  dassQuestions = [
    { number: 1, model: "windDown", question: "I found it hard to wind down" },
    {
      number: 2,
      model: "mouthDryness",
      question: "I was aware of dryness of my mouth",
    },
    {
      number: 3,
      model: "positiveFeeling",
      question: "I couldn’t seem to experience any positive feeling at all",
    },
    {
      number: 4,
      model: "breathingProblems",
      question:
        "I experienced breathing difficulty (e.g. excessively rapid breathing, breathlessness in the absence of physical exertion)",
    },
    {
      number: 5,
      model: "initiativeDoThings",
      question: "I found it difficult to work up the initiative to do things",
    },
    {
      number: 6,
      model: "overreact",
      question: "I tended to overreact to situations",
    },
    {
      number: 7,
      model: "trembling",
      question: "I experienced trembling (e.g. in the hands)",
    },
    {
      number: 8,
      model: "nervousEnergy",
      question: "I felt that I was using a lot of nervous energy",
    },
    {
      number: 9,
      model: "panicFoolMyself",
      question:
        "I was worried about situations in which I might panic and make a fool of myself",
    },
    {
      number: 10,
      model: "nothingLookForward",
      question: "I felt that I had nothing to look forward to",
    },
    {
      number: 11,
      model: "agitated",
      question: "I found myself getting agitated",
    },
    {
      number: 12,
      model: "relaxDifficult",
      question: "I found it difficult to relax",
    },
    {
      number: 13,
      model: "downHeartedBlue",
      question: "I felt down-hearted and blue",
    },
    {
      number: 14,
      model: "intolerant",
      question:
        "I was intolerant of anything that kept me from getting on with what I was doing",
    },
    {
      number: 15,
      model: "clostToPanic",
      question: "I felt I was close to panic",
    },
    {
      number: 16,
      model: "enthusiastic",
      question: "I was unable to become enthusiastic about anything",
    },
    {
      number: 17,
      model: "worthPerson",
      question: "I felt I wasn’t worth much as a person",
    },
    {
      number: 18,
      model: "touchy",
      question: "I felt that I was rather touchy",
    },
    {
      number: 19,
      model: "heartAware",
      question: `I was aware of the action of my heart in the absence of
    physical exertion (e.g. a sense of heart rate increase, heart missing a beat)`,
    },
    {
      number: 20,
      model: "scared",
      question: "I felt scared without any good reason",
    },
    {
      number: 21,
      model: "meaninglessLife",
      question: "I felt that life was meaningless",
    },
  ];

  pseqQuestions = [
    {
      number: 1,
      model: "enjoyDespitePain",
      question: "I can enjoy things, despite the pain",
    },
    {
      number: 2,
      model: "doHouseholdChores",
      question:
        "I can do most of the household chores (e.g. tidying up, washing dishes, etc.) despite the pain",
    },
    {
      number: 3,
      model: "socialiseDespitePain",
      question:
        "I can socialise with my friends or family members as often as I used to do, despite the pain",
    },
    {
      number: 4,
      model: "copeWithPain",
      question: "I can cope with my pain in most situations",
    },
    {
      number: 5,
      model: "doSomeWork",
      question:
        "I can do some form of work, despite the pain (“work” includes housework, paid and unpaid work)",
    },
    {
      number: 6,
      model: "hobbiesActivities",
      question:
        "I can still do many of the things I enjoy doing, such as hobbies or leisure activity, despite the pain",
    },
    {
      number: 7,
      model: "painWithoutMedication",
      question: "I can cope with my pain without medication",
    },
    {
      number: 8,
      model: "accomplishGoals",
      question:
        "I can still accomplish most of my goals in life, despite the pain",
    },
    {
      number: 9,
      model: "normalLifestyle",
      question: "I can live a normal lifestyle, despite the pain",
    },
    {
      number: 10,
      model: "moreActive",
      question: "I can gradually become more active, despite the pain",
    },
  ];

  pcsQuestions = [
    {
      number: 1,
      model: "worryPainEnd",
      question: "I worry all the time about whether the pain will end",
    },
    { number: 2, model: "cantGoOn", question: "I feel I can’t go on" },
    {
      number: 3,
      model: "neverGetBetter",
      question: "It’s terrible and I think it’s never going to get any better",
    },
    {
      number: 4,
      model: "awfulOverwhelming",
      question: "It’s awful and I feel it overwhelms me",
    },
    {
      number: 5,
      model: "cantStandIt",
      question: "I feel I can’t stand it anymore",
    },
    {
      number: 6,
      model: "afraidPainWorse",
      question: "I become afraid that the pain will get worse",
    },
    {
      number: 7,
      model: "thinkingPainfulEvents",
      question: "I keep thinking of other painful events",
    },
    {
      number: 8,
      model: "anxiouslyPainGoAway",
      question: "I anxiously want the pain to go away",
    },
    {
      number: 9,
      model: "keepOutOfMind",
      question: "I can’t seem to keep it out of my mind",
    },
    {
      number: 10,
      model: "howMuchItHurts",
      question: "I keep thinking about how much it hurts",
    },
    {
      number: 11,
      model: "painToStop",
      question: "I keep thinking about how badly I want the pain to stop",
    },
    {
      number: 12,
      model: "reducePain",
      question: "There’s nothing I can do to reduce the intensity of the pain",
    },
    {
      number: 13,
      model: "wonderSeriousHappen",
      question: "I wonder whether something serious may happen",
    },
  ];

  updateFormUserCompletion(
    formUserCompletion: FormUserCompletionResponse
  ): void {
    this.formUserCompletionUpdated.next(formUserCompletion);
  }

  saveIntakeForm(data: any) {
    const final = new IntakeFormItem();

    this.phodaFormsService.saveIntakeForm(final).subscribe((res) => {
      const snackbarMessage = "hello";
      this.snackbarService.openSnackBar(snackbarMessage, 3000);
    });
  }
}
